import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type {
  Lists, Card, CardId, MainFaceId,
} from '@vlabs/api/artemy/model';
import { DeleteCardsIconButtonPoint3 } from '@vlabs/monokit/features/delete-cards';
import { TableView, TableViewSelectable } from '@vlabs/monokit/shared/ui/table-view';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { dateFormatter } from '@vlabs/uikit/components/table-view/cell/formatters/date';
import { useTranslation } from 'react-i18next';

import { CardIdCell } from './card-id-cell/CardIdCell';
import { CardListTableViewHead } from './CardListTableViewHead';
import {
  photoCell, cardIdCell, userDataCell, createTimeCell, actionCell,
  dividerCell, renderHeader, renderCell, listsCell,
} from './cells';
import { FaceCell } from './face-cell';
import { ListsCell } from './lists-cell/ListsCell';
import { ViewCardIconButton } from '../../view-card';

interface CardListTableViewProps {
  data: Card[];
  onSelect: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedIds: Record<string, boolean>;
}

function CardListTableView({
  selectedIds,
  onSelect,
  ...tableViewProps
}: CardListTableViewProps): React.JSX.Element {
  const { t } = useTranslation();
  const canDeleteCard = useHasPermission('card', 'delete');
  const canViewCardDetails = usePageAccess('cards_details');

  const allColumns: ColumnDef<Card>[] = useMemo(() => ([
    {
      accessorKey: 'main_face_id',
      header: renderHeader<string>(photoCell, t('card-list-page:table.columns.main_face_id')),
      cell: renderCell<MainFaceId>(photoCell, (ctx) => (
        <FaceCell
          data-testid={ctx.row.original.card_id}
          faceId={ctx.getValue()}
          key={`face_${ctx.getValue()}`}
        />
      )),
    },
    {
      accessorKey: 'card_id',
      header: renderHeader(cardIdCell, t('card-list-page:table.columns.card_id')),
      cell: renderCell<CardId>(cardIdCell, (ctx) => (<CardIdCell cardId={ctx.getValue()} />)),
    },
    {
      accessorKey: 'user_data.fio',
      header: renderHeader(userDataCell, t('card-list-page:table.columns.user_data')),
      cell: renderCell(userDataCell, (ctx) => (typeof ctx.getValue() === 'object' ? JSON.stringify(ctx.getValue()) : ctx.getValue())),
    },
    {
      accessorKey: 'create_time',
      header: renderHeader(createTimeCell, t('card-list-page:table.columns.create_time')),
      cell: renderCell(createTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    {
      accessorKey: 'lists',
      header: renderHeader(listsCell, t('card-list-page:table.columns.lists')),
      cell: renderCell<Lists>(listsCell, (ctx) => (
        <ListsCell
          data-testid={ctx.row.original.lists}
          key={`lists_${ctx.getValue()}`}
          lists={ctx.getValue()}
        />
      )),
    },
    ...(canViewCardDetails ? [{
      accessorKey: 'card_id' as const,
      id: 'viewPerson',
      header: renderHeader(actionCell),
      cell: renderCell<CardId>(actionCell, (ctx) => (
        <ViewCardIconButton
          cardId={ctx.getValue()}
          data-testid={ctx.row.original.card_id}
          key={`viewPerson_${ctx.getValue()}`}
          shape="round"
          size="medium"
          variant="flat"
        />
      )),
    }] : []),
    ...(canDeleteCard ? [{
      accessorKey: 'card_id' as const,
      id: 'deletePerson',
      header: renderHeader(actionCell),
      cell: renderCell<CardId>(actionCell, (ctx) => (
        <DeleteCardsIconButtonPoint3
          cardId={ctx.getValue()}
          data-testid={ctx.row.original.card_id}
          key={`deletePerson_${ctx.getValue()}`}
          shape="round"
          size="medium"
          variant="flat"
        />
      )),
    }] : []),
    { cell: dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<Card>[]), [t, canDeleteCard, canViewCardDetails]);

  let TableComponent: typeof TableView = TableView;
  if (canDeleteCard) {
    TableComponent = TableViewSelectable as typeof TableView;
  }
  let selectProps = {};
  if (canDeleteCard) {
    selectProps = {
      rowSelection: selectedIds,
      onRowSelectionChange: onSelect,
    };
  }

  return (
    <TableComponent<Card>
      {...tableViewProps}
      {...selectProps}
      columns={allColumns}
      getRowId={(row) => row.card_id as string}
      TableHead={CardListTableViewHead}
    />
  );
}

export { CardListTableView };
